import { apiRequest } from "../lib/apiClient";
import { API_BASE_URL } from "../env";
import type { ExpiringSoonItemOut, LowStockItemOut, PnlOut, SalesExpenseSummaryOut, SalesSummaryOut } from "./types";

export async function getExpiringSoon(accessToken: string, days = 30, branchId?: string): Promise<ExpiringSoonItemOut[]> {
  return apiRequest<ExpiringSoonItemOut[]>("/reports/expiring-soon", { accessToken, query: { days, branch_id: branchId } });
}

export async function getLowStock(accessToken: string, branchId?: string): Promise<LowStockItemOut[]> {
  return apiRequest<LowStockItemOut[]>("/reports/low-stock", { accessToken, query: { branch_id: branchId } });
}

export interface DateRangeQuery {
  /** ISO datetime string, e.g. `2026-07-01T00:00:00`. Backend param is `datetime`
   * (`app/api/v1/reports.py`), not `date` — send midnight-of-day for a plain date. */
  dateFrom?: string;
  dateTo?: string;
  branchId?: string;
}

export async function getSalesSummary(accessToken: string, range: DateRangeQuery = {}): Promise<SalesSummaryOut> {
  return apiRequest<SalesSummaryOut>("/reports/sales-summary", {
    accessToken,
    query: { branch_id: range.branchId, date_from: range.dateFrom, date_to: range.dateTo },
  });
}

/** `GET /reports/sales-expense-summary` — fixed 8-period (today/yesterday/this & last
 * week/month/year) sales-vs-prorated-expenses cards. No params: periods and tenant scope
 * are fixed server-side (matches `pnl()`, which never supported branch filtering either). */
export async function getSalesExpenseSummary(accessToken: string): Promise<SalesExpenseSummaryOut> {
  return apiRequest<SalesExpenseSummaryOut>("/reports/sales-expense-summary", { accessToken });
}

export async function getPnl(accessToken: string, range: Omit<DateRangeQuery, "branchId"> = {}): Promise<PnlOut> {
  return apiRequest<PnlOut>("/reports/pnl", {
    accessToken,
    query: { date_from: range.dateFrom, date_to: range.dateTo },
  });
}

/** `GET /reports/accounting-export?format=csv` — OWNER/ACCOUNTANT only (§14.2). One row per
 * `TransactionLeg`, never aggregated — a real GL export for a bookkeeper/external accountant.
 * Returns a raw CSV `Response`, not JSON, so this bypasses `apiRequest`'s JSON parsing. */
export async function exportAccountingCsv(accessToken: string, range: Omit<DateRangeQuery, "branchId"> = {}): Promise<Blob> {
  const url = new URL(`${API_BASE_URL}/reports/accounting-export`);
  url.searchParams.set("format", "csv");
  if (range.dateFrom) url.searchParams.set("date_from", range.dateFrom);
  if (range.dateTo) url.searchParams.set("date_to", range.dateTo);
  const response = await fetch(url.toString(), { headers: { Authorization: `Bearer ${accessToken}` } });
  if (!response.ok) throw new Error(`Accounting export failed with status ${response.status}`);
  return response.blob();
}
